import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { NowCompactProductCard } from '../now/NowCompactProductCard';
import { ShoppingCartIcon, MinusIcon, PlusIcon, ClockIcon, XIcon, HeartIcon, StarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { SmartImage } from './SmartImage';
import { haptics } from '../../lib/utils/haptics';
import { useStore } from '../../lib/store';
import { motion, AnimatePresence } from 'framer-motion';
import { useHorizontalScroll } from '../../lib/hooks/useHorizontalScroll';
interface EnhancedProductCardProps {
  product: {
    id: number;
    name: string;
    brand?: string;
    price: number;
    originalPrice?: number;
    discount?: number;
    rating?: number;
    reviewCount?: number;
    image: string;
    isBestSeller?: boolean;
    isNew?: boolean;
    expressDelivery?: boolean;
    isGlobal?: boolean;
    endingSoon?: boolean;
  };
}
export function EnhancedProductCard({
  product
}: EnhancedProductCardProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNowDealsContext = window.location.pathname === '/offers' && searchParams.get('from') === 'now';
  const {
    cart,
    addToCart,
    updateQuantity,
    toggleWishlist,
    isInWishlist
  } = useStore();
  const cartItem = cart.find(item => item.id === product.id);
  const quantity = cartItem?.quantity || 0;
  const isWishlisted = isInWishlist(product.id);
  const [showDetailsSheet, setShowDetailsSheet] = useState(false);
  const isMobile = window.innerWidth <= 768;
  const displayDiscount = product.discount || (product.originalPrice ? Math.round((product.originalPrice - product.price) / product.originalPrice * 100) : null);
  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    haptics.success();
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      brand: product.brand
    });
    toast.custom(t => <motion.div initial={{
      opacity: 0,
      y: 50,
      scale: 0.9
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.9
    }} className="flex min-w-[320px] items-center gap-4 rounded-2xl border-2 border-brand bg-white p-4 shadow-2xl" data-id="element-5318" data-mp="tcl08swl7r99">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-brand-light" data-id="element-5319" data-mp="fc1s7ls4gl73">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          damping: 12
        }} data-id="element-5320" data-mp="ndw9eed69gm">
              <ShoppingCartIcon className="h-6 w-6 text-brand" data-id="element-5321" data-mp="3rtfeq80ns5ca" />
            </motion.div>
          </div>
          <div className="flex-1" data-id="element-5322" data-mp="3954z8byksxrh">
            <p className="text-sm font-bold text-gray-900" data-id="element-5323" data-mp="396694d3uve7m">Added to Cart!</p>
            <p className="text-xs text-gray-500 line-clamp-1" data-id="element-5324" data-mp="346l9s1krx3ud">{product.name}</p>
          </div>
          <button onClick={() => {
        toast.dismiss(t);
        navigate('/cart');
      }} className="bg-[#1E2B55] text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-[#162041] transition-colors" data-id="element-5325" data-mp="3i5l0rfvrdutp">
            View Cart
          </button>
        </motion.div>, {
      duration: 3000
    });
  };
  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    haptics.light();
    updateQuantity(product.id, quantity + 1);
  };
  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    haptics.light();
    updateQuantity(product.id, quantity - 1);
  };
  const handleWishlist = (e: React.MouseEvent) => {
    e.stopPropagation();
    haptics.selection();
    toggleWishlist({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      brand: product.brand
    });
  };
  const handleCardClick = () => {
    haptics.impact();
    if (isMobile) {
      setShowDetailsSheet(true);
    } else {
      navigate(`/product/${product.id}`);
    }
  };
  if (isNowDealsContext) {
    return <NowCompactProductCard product={product} data-id="element-5326" data-mp="17i7nqlgx9aqt" />;
  }
  return <>
      <div onClick={handleCardClick} className="bg-white rounded-lg overflow-hidden active:scale-[0.98] transition-all group cursor-pointer h-full flex flex-col border border-gray-100" data-id="element-5327" data-mp="au1m87faqygr">
        {/* Product Image */}
        <div className="relative aspect-[4/5] bg-gray-50 overflow-hidden flex-shrink-0" data-id="element-5328" data-mp="jsn0hqdqjrbq">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-5329" data-mp="oum6l80v12cd" />

          {/* Ending Soon Badge */}
          {product.endingSoon && <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-red-50 text-red-600 border border-red-100 text-[10px] font-semibold px-2 py-1 rounded-md flex items-center gap-1 whitespace-nowrap z-10" data-id="element-5330" data-mp="3kqicyg8nmjwy">
              <ClockIcon className="w-3 h-3 animate-pulse" data-id="element-5331" data-mp="3azdzjkymn748" />
              ENDING SOON
            </div>}

          {/* Top-left: discount / best seller / new */}
          <div className="absolute top-2 left-2 flex flex-col gap-1.5 items-start z-10" data-id="element-5332" data-mp="320adc845ybli">
            {product.discount && <div className="bg-red-50 text-red-600 border border-red-100 text-[10px] font-semibold px-2 py-0.5 rounded-md" data-id="element-5333" data-mp="2wzv15jvmba54">
                -{product.discount}%
              </div>}
            {product.isBestSeller && <div className="bg-yellow-50 text-yellow-700 border border-yellow-100 text-[10px] font-semibold px-2 py-0.5 rounded-md" data-id="element-5334" data-mp="2n4kweodu4onn">
                Best Seller
              </div>}
            {product.isNew && <div className="bg-green-50 text-green-700 border border-green-100 text-[10px] font-semibold px-2 py-0.5 rounded-md" data-id="element-5335" data-mp="2fqtugonmo47w">
                New
              </div>}
          </div>

          {/* Floating Wishlist Button */}
          <button onClick={handleWishlist} className="absolute top-2 right-2 w-8 h-8 rounded-md bg-white/80 backdrop-blur-sm flex items-center justify-center transition-colors border border-gray-100 z-10" data-id="element-5336" data-mp="25uqvtsrzinpp">
            <HeartIcon className={`w-4 h-4 ${isWishlisted ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} data-id="element-5337" data-mp="33gwmx5vgfc71" />
          </button>
        </div>
        {/* Product Info */}
        <div className="p-2 flex flex-col flex-grow" data-id="element-5338" data-mp="2tiqiagi9jryh">
          {/* Top Row: Rating & Add to Cart */}
          <div className="flex items-center justify-between mb-2 min-h-[32px]" data-id="element-5339" data-mp="1e287swvhbg5b">
            {/* Rating */}
            <div className="flex items-center gap-1 text-gray-400 text-[10px]" data-id="element-5340" data-mp="1i3bkjai8cw2b">
              {product.rating !== undefined && product.reviewCount !== undefined && product.reviewCount > 0 ? <>
                  <span className="font-bold text-gray-600" data-id="element-5341" data-mp="15kfypa1pzl5i">
                    {product.rating}
                  </span>
                  <StarIcon className="w-2.5 h-2.5 fill-yellow-400 text-yellow-400" data-id="element-5342" data-mp="1kg5nffyaicdx" />
                  <span className="opacity-80" data-id="element-5343" data-mp="1fjx2flxevoyu">({product.reviewCount})</span>
                </> : <span className="opacity-0" data-id="element-5344" data-mp="rr88nmtaexpx">0.0</span>}
            </div>

            {/* Add to Cart Button */}
            <div className="relative" data-id="element-5345" data-mp="11j4a97ul15yw">
              <AnimatePresence mode="wait" data-id="element-5346" data-mp="11k5spxdxguqn">
                {quantity === 0 ? <motion.button key="add-to-cart" initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.95
              }} whileTap={{
                scale: 0.95
              }} onClick={handleAddToCart} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-700 hover:border-[#1E2B55] hover:text-[#1E2B55] transition-colors" data-id="element-5347" data-mp="wrmhv86mkdpj">
                    <div className="relative" data-id="element-5348" data-mp="6iclorajmumu">
                      <ShoppingCartIcon className="w-4 h-4" strokeWidth={1.5} data-id="element-5349" data-mp="6h1lt2k7y710" />
                      <PlusIcon className="w-2.5 h-2.5 absolute -bottom-1 -right-1 bg-white rounded-sm text-gray-900" strokeWidth={2.5} data-id="element-5350" data-mp="badzwir9uvfi" />
                    </div>
                  </motion.button> : <motion.div key="quantity-stepper" initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.95
              }} className="h-8 bg-[#1E2B55] text-white rounded-lg flex items-center justify-between overflow-hidden shadow-sm px-1" onClick={e => e.stopPropagation()} data-id="element-5351" data-mp="kblvuu6bfk6f">
                    <button onClick={handleDecrement} className="w-6 h-full flex items-center justify-center active:bg-[#162041] transition-colors rounded-md" aria-label="Decrease quantity" data-id="element-5352" data-mp="2ogaqxvt12c9v">
                      <MinusIcon className="w-3 h-3" data-id="element-5353" data-mp="2eoes2ij4ai4l" />
                    </button>
                    <motion.span key={quantity} initial={{
                  scale: 1.2,
                  fontWeight: 700
                }} animate={{
                  scale: 1,
                  fontWeight: 700
                }} className="text-xs w-4 text-center" data-id="element-5354" data-mp="3c7pzw7fb7ru3">
                      {quantity}
                    </motion.span>
                    <button onClick={handleIncrement} className="w-6 h-full flex items-center justify-center active:bg-[#162041] transition-colors rounded-md" aria-label="Increase quantity" data-id="element-5355" data-mp="wzi1uwmq2w54">
                      <PlusIcon className="w-3 h-3" data-id="element-5356" data-mp="1kpm532w00tvl" />
                    </button>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          {/* Brand */}
          {product.brand && <p className="text-[10px] text-gray-400 mb-0.5 font-medium" data-id="element-5357" data-mp="1tozgi13onsiy">
              {product.brand}
            </p>}
          {/* Product Name */}
          <h3 className="text-sm font-medium text-gray-900 line-clamp-2 mb-2 leading-tight group-hover:text-[#1E2B55] transition-colors flex-grow" data-id="element-5358" data-mp="28ek99dyu867g">
            {product.name}
          </h3>
          
          {/* Price */}
          <div className="flex flex-col mt-auto" data-id="element-5359" data-mp="1zffaq7na0cva">
            {/* Old Price & Discount */}
            <div className="flex items-center gap-2 min-h-[18px] mb-0.5" data-id="element-5360" data-mp="23d4qxto1odsr">
              {product.originalPrice && <>
                  <span className="text-[10px] text-gray-400 line-through" data-id="element-5361" data-mp="1uermftqfma4r">
                    {product.originalPrice} SAR
                  </span>
                  {displayDiscount && <span className="bg-red-100 text-red-600 text-[10px] font-bold px-1.5 py-0.5 rounded-md" data-id="element-5362" data-mp="1ko6q6cy8dhp0">
                      -{displayDiscount}%
                    </span>}
                </>}
            </div>
            {/* Final Price */}
            <span className={`text-base font-bold ${product.originalPrice ? 'text-red-500' : 'text-gray-900'}`} data-id="element-5363" data-mp="1bpl0zyy974f9">
              {product.price} SAR
            </span>
          </div>
        </div>
      </div>

      {/* Mobile Details Bottom Sheet */}
      <AnimatePresence data-id="element-5364" data-mp="33nnxfub843je">
        {showDetailsSheet && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={e => {
          e.stopPropagation();
          setShowDetailsSheet(false);
        }} className="fixed inset-0 bg-black/50 z-[100] md:hidden" data-id="element-5365" data-mp="2zmkgf4hfw1gl" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-[101] md:hidden max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-5366" data-mp="3ie4ul1gts7ah">
              <div className="flex justify-center pt-3 pb-2 flex-shrink-0" data-id="element-5367" data-mp="106hmex1ceb71">
                <div className="w-12 h-1.5 bg-gray-300 rounded-full" data-id="element-5368" data-mp="155kms46dnt8o" />
              </div>

              <div className="flex justify-between items-center px-4 pb-2 border-b border-gray-100 flex-shrink-0" data-id="element-5369" data-mp="1a4f8pezn5xx0">
                <h3 className="font-bold text-lg" data-id="element-5370" data-mp="1f2yvtipz1fqy">Deal Details</h3>
                <button onClick={() => setShowDetailsSheet(false)} className="p-2 hover:bg-gray-100 rounded-full" data-id="element-5371" data-mp="1f1nrnhsm65zb">
                  <XIcon className="w-5 h-5" data-id="element-5372" data-mp="3gwfzycew7ygy" />
                </button>
              </div>

              <div className="overflow-y-auto p-4 flex-grow" data-id="element-5373" data-mp="3kx8975nrm3d8">
                <div className="aspect-square bg-gray-50 rounded-xl overflow-hidden mb-4 relative" data-id="element-5374" data-mp="2plqwnrehqbwd">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-5375" data-mp="2ll80xm9h8low" />
                  {product.discount && <div className="absolute top-3 left-3 bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full" data-id="element-5376" data-mp="2umemwauwaopj">
                      -{product.discount}%
                    </div>}
                </div>

                {product.brand && <p className="text-sm text-gray-500 uppercase tracking-wide mb-1" data-id="element-5377" data-mp="2glc0fwzznce3">
                    {product.brand}
                  </p>}
                <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-5378" data-mp="2bssjqr608152">
                  {product.name}
                </h2>

                <div className="bg-red-50 rounded-xl p-4 mb-6 border border-red-100" data-id="element-5379" data-mp="1j67sdqpj3p1i">
                  <div className="flex justify-between items-end" data-id="element-5380" data-mp="1j4dhl57z1db5">
                    <div data-id="element-5381" data-mp="3us09l9c0po5d">
                      <p className="text-sm text-red-600 font-medium mb-1" data-id="element-5382" data-mp="3urqghcvof0vg">
                        Special Deal Price
                      </p>
                      <div className="flex items-baseline gap-2" data-id="element-5383" data-mp="3ltoask0zzk1u">
                        <span className="text-3xl font-bold text-red-600" data-id="element-5384" data-mp="3pugk1d9vdoy4">
                          {product.price}
                        </span>
                        <span className="text-red-600 font-medium" data-id="element-5385" data-mp="hfyg9nqyovkb">SAR</span>
                      </div>
                    </div>
                    {product.originalPrice && <div className="text-right" data-id="element-5386" data-mp="8erw94p3t2mb">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5387" data-mp="8g30t6qdieeh">
                          Regular Price
                        </p>
                        <span className="text-lg text-gray-400 line-through" data-id="element-5388" data-mp="2t3oeh9kkbfal">
                          {product.originalPrice} SAR
                        </span>
                      </div>}
                  </div>
                </div>

                <div className="space-y-4" data-id="element-5389" data-mp="13p6mngfadzn9">
                  <button onClick={() => {
                navigate(`/product/${product.id}`);
                setShowDetailsSheet(false);
              }} className="w-full bg-gray-100 text-gray-900 font-bold py-3.5 rounded-xl" data-id="element-5390" data-mp="ynhbm0s2dlz6">
                    View Full Product Details
                  </button>

                  <button onClick={e => {
                handleAddToCart(e);
                setShowDetailsSheet(false);
              }} className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3.5 font-bold text-white shadow-lg shadow-brand/20 transition-colors duration-150 hover:bg-brand-dark" data-id="element-5391" data-mp="fxhp9lnlykzg">
                    <ShoppingCartIcon className="w-5 h-5" data-id="element-5392" data-mp="21augpuo9s5vm" />
                    {quantity > 0 ? 'Added to Cart' : 'Add to Cart'}
                  </button>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}